import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Bot,
  ExternalLink,
  Lock,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  areStringArraysEqual,
  buildIssueCreationSettingsDraft,
  buildRepoIssueBotSelectionDrafts,
  getRepoIssueBotSelectionUpdates,
  isIssueBotReferenced,
  normalizeBotIdsForCatalog,
  toggleCatalogBotSelection,
  type RepoIssueBotSelectionDraft,
  type RepoIssueBotSelectionMode,
} from "@/github-app/issue-creation-bot-state";
import { useOrganizationCanManage } from "@/hooks/organizations/use-organization-can-manage";
import { useTRPC } from "@/infrastructure/trpc/react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import { Switch } from "@/shared/ui/switch";

export const Route = createFileRoute(
  "/app/organization/$id/settings/github/issue-creation"
)({
  loader: async ({ context, params }) => {
    const installation = await context.queryClient.query(
      context.trpc.githubApp.getInstallation.queryOptions({
        organizationId: params.id,
      })
    );

    if (installation) {
      await Promise.all([
        context.queryClient
          .query(
            context.trpc.githubApp.listRepositories.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.getRepoAssignees.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.listIssueBots.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
      ]);
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { canManage } = useOrganizationCanManage(id);

  const {
    data: installation,
    isLoading: isLoadingInstallation,
  } = useSuspenseQuery(
    trpc.githubApp.getInstallation.queryOptions({
      organizationId: id,
    })
  );

  const { data: repositories } = useQuery({
    ...trpc.githubApp.listRepositories.queryOptions({
      organizationId: id,
    }),
    enabled: !!installation,
  });

  const { data: repoAssignees, refetch: refetchAssignees } =
    useQuery({
      ...trpc.githubApp.getRepoAssignees.queryOptions({
        organizationId: id,
      }),
      enabled: !!installation,
    });

  const { data: issueBots } = useQuery({
    ...trpc.githubApp.listIssueBots.queryOptions({
      organizationId: id,
    }),
    enabled: !!installation,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [autoIssueCreation, setAutoIssueCreation] =
    useState(installation?.autoIssueCreation ?? false);
  const [maxIssuesPerHour, setMaxIssuesPerHour] = useState(
    installation?.maxIssuesPerHour?.toString() ?? "10"
  );
  const [selectedRepo, setSelectedRepo] = useState(
    installation?.defaultRepositoryOwner &&
      installation?.defaultRepositoryName
      ? `${installation.defaultRepositoryOwner}/${installation.defaultRepositoryName}`
      : ""
  );
  const [defaultBotIds, setDefaultBotIds] = useState<
    string[]
  >([]);
  const [newBotHandle, setNewBotHandle] = useState("");
  const [
    repoBotSelectionDrafts,
    setRepoBotSelectionDrafts,
  ] = useState<Record<string, RepoIssueBotSelectionDraft>>(
    {}
  );
  const [syncedInputs, setSyncedInputs] = useState<{
    settingsKey: string | null;
    repoKey: string | null;
    catalog: typeof issueBots;
  }>({
    settingsKey: null,
    repoKey: null,
    catalog: issueBots,
  });

  const catalogIssueBots = issueBots ?? [];
  const sortedRepos = useMemo(
    () =>
      [...(repositories ?? [])].sort((left, right) =>
        left.fullName.localeCompare(right.fullName)
      ),
    [repositories]
  );
  const settingsDraftFromServer = useMemo(
    () =>
      installation
        ? buildIssueCreationSettingsDraft(
            installation,
            catalogIssueBots
          )
        : null,
    [installation, catalogIssueBots]
  );
  const repoBotSelectionsFromServer = useMemo(
    () =>
      buildRepoIssueBotSelectionDrafts(
        sortedRepos,
        repoAssignees ?? [],
        catalogIssueBots
      ),
    [sortedRepos, repoAssignees, catalogIssueBots]
  );
  const issueBotHandleById = useMemo(
    () =>
      new Map(
        catalogIssueBots.map((issueBot) => [
          issueBot.id,
          issueBot.handle,
        ])
      ),
    [catalogIssueBots]
  );
  const installationSettingsSyncKey = useMemo(
    () =>
      installation
        ? JSON.stringify({
            autoIssueCreation:
              installation.autoIssueCreation,
            maxIssuesPerHour: installation.maxIssuesPerHour,
            defaultRepositoryOwner:
              installation.defaultRepositoryOwner,
            defaultRepositoryName:
              installation.defaultRepositoryName,
            defaultBotIds: installation.defaultBotIds,
          })
        : null,
    [installation]
  );
  const repoBotSelectionsSyncKey = useMemo(
    () =>
      repositories === undefined ||
      repoAssignees === undefined
        ? null
        : JSON.stringify({
            repositories: sortedRepos.map(
              (repo) => repo.fullName
            ),
            repoSelections: [...repoAssignees]
              .sort((left, right) =>
                left.repositoryFullName.localeCompare(
                  right.repositoryFullName
                )
              )
              .map((repoAssignee) => ({
                repositoryFullName:
                  repoAssignee.repositoryFullName,
                assignees: repoAssignee.assignees,
                botSelectionMode:
                  repoAssignee.botSelectionMode,
                selectedBotIds: repoAssignee.selectedBotIds,
              })),
          }),
    [repoAssignees, repositories, sortedRepos]
  );

  const hasChanges = settingsDraftFromServer
    ? autoIssueCreation !==
        settingsDraftFromServer.autoIssueCreation ||
      maxIssuesPerHour !==
        settingsDraftFromServer.maxIssuesPerHour ||
      selectedRepo !==
        settingsDraftFromServer.selectedRepo ||
      !areStringArraysEqual(
        defaultBotIds,
        settingsDraftFromServer.defaultBotIds
      )
    : false;

  const settingsKey =
    issueBots === undefined
      ? null
      : installationSettingsSyncKey;
  const repoKey =
    issueBots === undefined
      ? null
      : repoBotSelectionsSyncKey;
  if (
    syncedInputs.settingsKey !== settingsKey ||
    syncedInputs.repoKey !== repoKey ||
    syncedInputs.catalog !== issueBots
  ) {
    setSyncedInputs({
      settingsKey,
      repoKey,
      catalog: issueBots,
    });
    if (
      settingsDraftFromServer &&
      issueBots !== undefined &&
      settingsKey !== null &&
      syncedInputs.settingsKey !== settingsKey
    ) {
      setAutoIssueCreation(
        settingsDraftFromServer.autoIssueCreation
      );
      setMaxIssuesPerHour(
        settingsDraftFromServer.maxIssuesPerHour
      );
      setSelectedRepo(settingsDraftFromServer.selectedRepo);
      setDefaultBotIds(
        settingsDraftFromServer.defaultBotIds
      );
    } else if (
      issueBots !== undefined &&
      syncedInputs.catalog !== issueBots
    ) {
      setDefaultBotIds((currentBotIds) => {
        const normalizedBotIds = normalizeBotIdsForCatalog(
          currentBotIds,
          catalogIssueBots
        );

        return areStringArraysEqual(
          currentBotIds,
          normalizedBotIds
        )
          ? currentBotIds
          : normalizedBotIds;
      });
    }
    if (
      issueBots !== undefined &&
      repoKey !== null &&
      syncedInputs.repoKey !== repoKey
    ) {
      setRepoBotSelectionDrafts({});
    } else if (
      issueBots !== undefined &&
      syncedInputs.catalog !== issueBots
    ) {
      setRepoBotSelectionDrafts((currentDrafts) => {
        let hasNormalizedDraft = false;
        const normalizedDrafts: Record<
          string,
          RepoIssueBotSelectionDraft
        > = {};

        for (const [
          repositoryFullName,
          draft,
        ] of Object.entries(currentDrafts)) {
          if (draft.botSelectionMode !== "custom") {
            normalizedDrafts[repositoryFullName] = draft;
            continue;
          }

          const normalizedSelectedBotIds =
            normalizeBotIdsForCatalog(
              draft.selectedBotIds,
              catalogIssueBots
            );

          if (
            areStringArraysEqual(
              draft.selectedBotIds,
              normalizedSelectedBotIds
            )
          ) {
            normalizedDrafts[repositoryFullName] = draft;
            continue;
          }

          hasNormalizedDraft = true;
          normalizedDrafts[repositoryFullName] = {
            ...draft,
            selectedBotIds: normalizedSelectedBotIds,
          };
        }

        return hasNormalizedDraft
          ? normalizedDrafts
          : currentDrafts;
      });
    }
  }

  const updateSettings = useMutation(
    trpc.githubApp.updateSettings.mutationOptions({
      onSuccess: async () => {
        toast.success("Settings updated");
        await queryClient.invalidateQueries({
          queryKey:
            trpc.githubApp.getInstallation.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to update settings"
        );
      },
    })
  );

  const createIssueBot = useMutation(
    trpc.githubApp.createIssueBot.mutationOptions({
      onSuccess: async () => {
        toast.success("Bot added to catalog");
        setNewBotHandle("");
        await queryClient.invalidateQueries({
          queryKey: trpc.githubApp.listIssueBots.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to create issue bot"
        );
      },
    })
  );

  const deleteIssueBot = useMutation(
    trpc.githubApp.deleteIssueBot.mutationOptions({
      onSuccess: async () => {
        toast.success("Bot removed from catalog");
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.githubApp.listIssueBots.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.githubApp.getInstallation.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.githubApp.getRepoAssignees.queryKey(),
          }),
        ]);
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to delete issue bot"
        );
      },
    })
  );

  const updateRepoAssignees = useMutation(
    trpc.githubApp.updateRepoAssignees.mutationOptions()
  );

  const getRepoAssignees = (repoFullName: string) => {
    const found = repoAssignees?.find(
      (repoAssignee) =>
        repoAssignee.repositoryFullName === repoFullName
    );

    return found?.assignees ?? [];
  };

  const totalAssignees = sortedRepos.reduce(
    (count, repo) =>
      count + getRepoAssignees(repo.fullName).length,
    0
  );

  const repoBotSelectionEntries = sortedRepos.map(
    (repo) => {
      const selection = repoBotSelectionDrafts[
        repo.fullName
      ] ??
        repoBotSelectionsFromServer[repo.fullName] ?? {
          assignees: [],
          botSelectionMode: "inherit",
          selectedBotIds: [],
        };

      return {
        repo,
        selection,
      };
    }
  );

  const currentRepoBotSelections = useMemo(
    () =>
      Object.fromEntries(
        repoBotSelectionEntries.map(
          ({ repo, selection }) => [
            repo.fullName,
            selection,
          ]
        )
      ) satisfies Record<
        string,
        RepoIssueBotSelectionDraft
      >,
    [repoBotSelectionEntries]
  );

  const repoBotSelectionUpdates = useMemo(
    () =>
      getRepoIssueBotSelectionUpdates({
        baselineSelections: repoBotSelectionsFromServer,
        nextSelections: currentRepoBotSelections,
        issueBots: catalogIssueBots,
      }),
    [
      repoBotSelectionsFromServer,
      currentRepoBotSelections,
      catalogIssueBots,
    ]
  );

  const defaultBotHandlesSummary = defaultBotIds
    .map((botId) => issueBotHandleById.get(botId))
    .filter((handle): handle is string => Boolean(handle));

  const parsedMaxIssuesPerHour = Number.parseInt(
    maxIssuesPerHour,
    10
  );
  const isMaxIssuesPerHourInvalid =
    Number.isNaN(parsedMaxIssuesPerHour) ||
    parsedMaxIssuesPerHour < 1 ||
    parsedMaxIssuesPerHour > 100;

  const updateRepoBotDraft = (
    repositoryFullName: string,
    updater: (
      currentDraft: RepoIssueBotSelectionDraft
    ) => RepoIssueBotSelectionDraft
  ) => {
    const fallbackDraft = repoBotSelectionsFromServer[
      repositoryFullName
    ] ?? {
      assignees: getRepoAssignees(repositoryFullName),
      botSelectionMode: "inherit",
      selectedBotIds: [],
    };

    setRepoBotSelectionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [repositoryFullName]: updater(
        currentDrafts[repositoryFullName] ?? fallbackDraft
      ),
    }));
  };

  const handleSave = () => {
    const [owner, repoName] = selectedRepo
      ? selectedRepo.split("/")
      : [null, null];

    updateSettings.mutate({
      organizationId: id,
      autoIssueCreation,
      maxIssuesPerHour: parsedMaxIssuesPerHour,
      defaultRepositoryOwner: owner ?? null,
      defaultRepositoryName: repoName ?? null,
      defaultBotIds,
    });
  };

  const handleCreateIssueBot = () => {
    if (!newBotHandle.trim()) {
      return;
    }

    createIssueBot.mutate({
      organizationId: id,
      handle: newBotHandle,
    });
  };

  const handleSaveRepoBotSelections = async () => {
    if (
      !canManage ||
      repoBotSelectionUpdates.length === 0
    ) {
      return;
    }

    try {
      for (const repoBotSelectionUpdate of repoBotSelectionUpdates) {
        await updateRepoAssignees.mutateAsync({
          organizationId: id,
          repositoryFullName:
            repoBotSelectionUpdate.repositoryFullName,
          assignees: repoBotSelectionUpdate.assignees,
          botSelectionMode:
            repoBotSelectionUpdate.botSelectionMode,
          selectedBotIds:
            repoBotSelectionUpdate.selectedBotIds,
        });
      }

      toast.success("Repository bot mentions updated");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update repository bot mentions"
      );
    } finally {
      await queryClient.invalidateQueries({
        queryKey:
          trpc.githubApp.getRepoAssignees.queryKey(),
      });
    }
  };

  if (isLoadingInstallation) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!installation) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/app/organization/$id/settings/github"
            params={{ id }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to GitHub
          </Link>
        </div>

        <Card>
          <CardContent
            variant="py12"
            className="text-center"
          >
            <Settings className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
            <h3 className="mb-2 font-medium">
              GitHub App Not Connected
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Connect the GitHub App first to configure
              issue creation settings.
            </p>
            <Link
              to="/app/organization/$id/settings/github"
              params={{ id }}
            >
              <Button>Connect GitHub App</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-lg font-semibold">
          Issue Creation
        </h3>
        <p className="text-sm text-muted-foreground">
          Configure automatic GitHub issue creation when
          problems are detected.
        </p>
      </div>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle variant="base">Settings</CardTitle>
          </div>
          <CardDescription>
            Control how and when issues are created.
          </CardDescription>
        </CardHeader>
        <CardContent variant="space6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-issue-creation">
                Automatic Issue Creation
              </Label>
              <p className="text-sm text-muted-foreground">
                Create GitHub issues when agents detect
                problems.
              </p>
            </div>
            <Switch
              id="auto-issue-creation"
              checked={autoIssueCreation}
              onCheckedChange={setAutoIssueCreation}
              disabled={!canManage}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="default-repo">
              Fallback Repository
            </Label>
            <Select
              value={selectedRepo}
              onValueChange={(value) => {
                setSelectedRepo(value ?? "");
              }}
              disabled={!canManage}
            >
              <SelectTrigger
                id="default-repo"
                className="w-full"
              >
                <SelectValue placeholder="Select a fallback repository" />
              </SelectTrigger>
              <SelectContent>
                {sortedRepos.map((repo) => (
                  <SelectItem
                    key={repo.id}
                    value={repo.fullName}
                  >
                    {repo.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              vivus first tries to match issues to a
              repository by namespace prefix (for example,
              `foo-*` → repo `foo`). If no match is found,
              this repository is used as a fallback.
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-0.5">
              <Label>
                Installation Default Bot Mentions
              </Label>
              <p className="text-sm text-muted-foreground">
                These bot mentions apply to repositories
                that inherit the installation defaults.
              </p>
            </div>
            {catalogIssueBots.length > 0 ? (
              <div className="space-y-2 rounded-lg border p-3">
                {catalogIssueBots.map((issueBot) => {
                  const inUse = isIssueBotReferenced({
                    botId: issueBot.id,
                    defaultBotIds,
                    repoSelections:
                      currentRepoBotSelections,
                  });

                  return (
                    <label
                      key={issueBot.id}
                      className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <Checkbox
                          checked={defaultBotIds.includes(
                            issueBot.id
                          )}
                          onCheckedChange={() => {
                            setDefaultBotIds(
                              (currentBotIds) =>
                                toggleCatalogBotSelection(
                                  currentBotIds,
                                  issueBot.id,
                                  catalogIssueBots
                                )
                            );
                          }}
                          disabled={!canManage}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            @{issueBot.handle}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {defaultBotIds.includes(
                              issueBot.id
                            )
                              ? "Selected as an installation default"
                              : "Available in the organization catalog"}
                          </p>
                        </div>
                      </div>
                      {inUse && (
                        <Badge variant="secondary">
                          In use
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
                Add at least one bot to the catalog below to
                configure installation defaults.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-issues">
              Max Issues Per Hour
            </Label>
            <Input
              id="max-issues"
              type="number"
              min="1"
              max="100"
              value={maxIssuesPerHour}
              onChange={(event) => {
                setMaxIssuesPerHour(event.target.value);
              }}
              disabled={!canManage}
              className="w-32"
            />
            <p className="text-sm text-muted-foreground">
              Rate limit for issue creation (1–100).
            </p>
          </div>

          {canManage && (
            <div className="pt-2">
              <Button
                onClick={handleSave}
                disabled={
                  !hasChanges ||
                  isMaxIssuesPerHourInvalid ||
                  updateSettings.isPending
                }
              >
                {updateSettings.isPending
                  ? "Saving..."
                  : "Save Settings"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            <CardTitle variant="base">
              Bot Catalog
            </CardTitle>
          </div>
          <CardDescription>
            Create reusable bot handles for installation
            defaults and repository-specific mention rules.
          </CardDescription>
        </CardHeader>
        <CardContent variant="space4">
          {canManage && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={newBotHandle}
                onChange={(event) => {
                  setNewBotHandle(event.target.value);
                }}
                placeholder="bot-handle"
                className="sm:max-w-xs"
              />
              <Button
                type="button"
                onClick={handleCreateIssueBot}
                disabled={
                  !newBotHandle.trim() ||
                  createIssueBot.isPending
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {createIssueBot.isPending
                  ? "Adding..."
                  : "Add Bot"}
              </Button>
            </div>
          )}

          {catalogIssueBots.length > 0 ? (
            <div className="divide-y overflow-hidden rounded-lg border">
              {catalogIssueBots.map((issueBot) => {
                const inUse = isIssueBotReferenced({
                  botId: issueBot.id,
                  defaultBotIds,
                  repoSelections: currentRepoBotSelections,
                });

                return (
                  <div
                    key={issueBot.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-medium">
                        @{issueBot.handle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {inUse
                          ? "Remove this bot from defaults and custom repo selections before deleting it."
                          : "Unused bots can be deleted from the catalog."}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {inUse && (
                        <Badge variant="secondary">
                          In use
                        </Badge>
                      )}
                      {canManage && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            deleteIssueBot.mutate({
                              organizationId: id,
                              botId: issueBot.id,
                            });
                          }}
                          disabled={
                            inUse ||
                            deleteIssueBot.isPending
                          }
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              No bots in the catalog yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <CardTitle variant="base">
                Repository Assignees
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  void refetchAssignees();
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {canManage && (
                <Link
                  to="/app/organization/$id/settings/github/assignees"
                  params={{ id }}
                >
                  <Button variant="outline" size="sm">
                    Manage
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <CardDescription>
            Set default assignees for automatic issue
            creation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedRepos.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {sortedRepos.length} repositories
                </span>
                <span className="text-muted-foreground">
                  {totalAssignees} total assignees
                </span>
              </div>
              <div className="max-h-75 divide-y overflow-hidden overflow-y-auto rounded-lg border">
                {sortedRepos.slice(0, 5).map((repo) => {
                  const currentAssignees = getRepoAssignees(
                    repo.fullName
                  );

                  return (
                    <div
                      key={repo.id}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium">
                              {repo.fullName}
                            </span>
                            {repo.private && (
                              <Lock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                            )}
                          </div>
                          {currentAssignees.length > 0 && (
                            <div className="mt-1 flex items-center gap-1.5">
                              {currentAssignees
                                .slice(0, 3)
                                .map((login) => (
                                  <span
                                    key={login}
                                    className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                                  >
                                    {login}
                                  </span>
                                ))}
                              {currentAssignees.length >
                                3 && (
                                <span className="text-xs text-muted-foreground">
                                  +
                                  {currentAssignees.length -
                                    3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-3">
                        {currentAssignees.length > 0 && (
                          <Badge
                            size="number"
                            variant="secondary"
                          >
                            {currentAssignees.length}
                          </Badge>
                        )}
                        <a
                          href={`https://github.com/${repo.fullName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
              {sortedRepos.length > 5 && (
                <p className="text-center text-xs text-muted-foreground">
                  +{sortedRepos.length - 5} more
                  repositories
                </p>
              )}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No repositories accessible. Configure
              repository access in GitHub App settings.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            <CardTitle variant="base">
              Repository Bot Mentions
            </CardTitle>
          </div>
          <CardDescription>
            Repositories can inherit the installation
            defaults or switch to a custom bot list. Custom
            mode with no selected bots disables mentions for
            that repository.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedRepos.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {defaultBotHandlesSummary.length > 0
                    ? `Installation defaults: ${defaultBotHandlesSummary.map((handle) => `@${handle}`).join(", ")}`
                    : "Installation defaults: no bot mentions"}
                </span>
                {canManage && (
                  <span className="text-muted-foreground">
                    {repoBotSelectionUpdates.length} unsaved
                    repo changes
                  </span>
                )}
              </div>

              <div className="max-h-130 space-y-3 overflow-y-auto rounded-lg border p-3">
                {repoBotSelectionEntries.map(
                  ({ repo, selection }) => {
                    const selectedBotHandles =
                      selection.selectedBotIds
                        .map((botId) =>
                          issueBotHandleById.get(botId)
                        )
                        .filter(
                          (handle): handle is string =>
                            Boolean(handle)
                        );
                    const assigneeSummary =
                      selection.assignees.length > 0
                        ? selection.assignees.join(", ")
                        : "No assignees configured";

                    return (
                      <div
                        key={repo.id}
                        className="space-y-3 rounded-lg border px-4 py-3"
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium">
                                {repo.fullName}
                              </span>
                              {repo.private && (
                                <Lock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                              )}
                              <Badge variant="secondary">
                                {selection.botSelectionMode ===
                                "inherit"
                                  ? "Inherit"
                                  : "Custom"}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Assignees: {assigneeSummary}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {selection.botSelectionMode ===
                              "inherit"
                                ? defaultBotHandlesSummary.length >
                                  0
                                  ? `Uses installation defaults: ${defaultBotHandlesSummary.map((handle) => `@${handle}`).join(", ")}`
                                  : "Uses installation defaults with no bot mentions"
                                : selectedBotHandles.length >
                                    0
                                  ? `Custom mentions: ${selectedBotHandles.map((handle) => `@${handle}`).join(", ")}`
                                  : "Custom mode with no selected bots disables mentions for this repository"}
                            </p>
                          </div>

                          <div className="w-full lg:w-64">
                            <Label
                              htmlFor={`repo-bot-mode-${repo.id}`}
                              className="sr-only"
                            >
                              Bot selection mode
                            </Label>
                            <Select
                              value={
                                selection.botSelectionMode
                              }
                              onValueChange={(value) => {
                                updateRepoBotDraft(
                                  repo.fullName,
                                  (currentDraft) => ({
                                    ...currentDraft,
                                    botSelectionMode:
                                      value as RepoIssueBotSelectionMode,
                                  })
                                );
                              }}
                              disabled={!canManage}
                            >
                              <SelectTrigger
                                id={`repo-bot-mode-${repo.id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inherit">
                                  Inherit installation
                                  defaults
                                </SelectItem>
                                <SelectItem value="custom">
                                  Custom repository
                                  selection
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {selection.botSelectionMode ===
                          "custom" && (
                          <div className="space-y-2">
                            {catalogIssueBots.length > 0 ? (
                              <>
                                <p className="text-xs text-muted-foreground">
                                  Select one or more catalog
                                  bots. Leave every bot
                                  unchecked to disable
                                  mentions for this
                                  repository.
                                </p>
                                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                  {catalogIssueBots.map(
                                    (issueBot) => (
                                      <label
                                        key={`${repo.id}-${issueBot.id}`}
                                        className="flex items-center gap-3 rounded-lg border px-3 py-2"
                                      >
                                        <Checkbox
                                          checked={selection.selectedBotIds.includes(
                                            issueBot.id
                                          )}
                                          onCheckedChange={() => {
                                            updateRepoBotDraft(
                                              repo.fullName,
                                              (
                                                currentDraft
                                              ) => ({
                                                ...currentDraft,
                                                selectedBotIds:
                                                  toggleCatalogBotSelection(
                                                    currentDraft.selectedBotIds,
                                                    issueBot.id,
                                                    catalogIssueBots
                                                  ),
                                              })
                                            );
                                          }}
                                          disabled={
                                            !canManage
                                          }
                                        />
                                        <span className="text-sm">
                                          @{issueBot.handle}
                                        </span>
                                      </label>
                                    )
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
                                Add a bot to the catalog
                                above to enable custom
                                repository selection.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>

              {canManage && (
                <div className="pt-2">
                  <Button
                    type="button"
                    onClick={() => {
                      void handleSaveRepoBotSelections();
                    }}
                    disabled={
                      repoBotSelectionUpdates.length ===
                        0 || updateRepoAssignees.isPending
                    }
                  >
                    {updateRepoAssignees.isPending
                      ? "Saving..."
                      : "Save Repository Bot Mentions"}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No repositories accessible. Configure
              repository access in GitHub App settings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
